import { useAccount, useMsal } from "@azure/msal-react"
import { SelectChangeEvent } from "@mui/material/Select"
import React, { FC, ChangeEvent, useMemo } from "react"

import { Dialog } from "src/components/common/dialog"
import { InputField } from "src/components/common/inputfield"
import { Select } from "src/components/common/select"
import { create } from "src/helpers/bem"

import { postPermission } from "src/queriesXRM/permission"
import {
  useBookingDetailPermissions,
  getBookingDetailPermissions,
  mergeBookingDetailPermission,
  mergeBookingDetailPermissions,
} from "src/states/bookingDetailPermissions"

import { TranslationMessages } from "src/translations"
import { xRMApiPermission, xRMApiPermissionRole } from "src/types/xRM"

import styles from "./PermissionTableActionsAddUserDialog.module.scss"

const bem = create(styles, "PermissionTableActionsAddUserDialog")

export type PermissionTableActionsAddUserDialogProps = {
  messages: TranslationMessages
}

export const PermissionTableActionsAddUserDialog: FC<
  PermissionTableActionsAddUserDialogProps
> = ({ messages }) => {
  const m = messages.pages.booking.detail.actions.permissions.actions.add
  const { accounts } = useMsal()
  const account = useAccount(accounts[0])
  const { addUser, permissionTableUser, currentSalesorderId } =
    useBookingDetailPermissions()
  let AddUser = {
    open: addUser.open,
    email: addUser.email,
    emailErrorText: addUser?.emailErrorText,
    role: addUser.role,
    roleErrorText: addUser?.roleErrorText,
  }

  let permissionItems =
    permissionTableUser === "MainExhibitor"
      ? [
          {
            key: "Administrator",
            value:
              messages.pages.booking.detail.actions.permissions.table.role.items
                .Administrator,
          },
        ]
      : []

  permissionItems =
    process.env.ENABLE_WEBSHOP === "true"
      ? [
          ...permissionItems,
          {
            key: "BuyOnOwnAccount",
            value:
              messages.pages.booking.detail.actions.permissions.table.role.items
                .BuyOnOwnAccount,
          },
          {
            key: "BuyOnForeignAccount",
            value:
              messages.pages.booking.detail.actions.permissions.table.role.items
                .BuyOnForeignAccount1,
          },
        ]
      : permissionItems

  const disableAddUserButton =
    addUser.email == "" ||
    addUser.role == null ||
    addUser.emailErrorText !== undefined ||
    addUser.roleErrorText !== undefined

  const handleChangeEmail = (e: ChangeEvent<HTMLInputElement>) => {
    AddUser = { ...AddUser, email: e.target.value }
    mergeBookingDetailPermissions({
      addUser: AddUser,
    })
  }

  const handleValidationEmail = (error?: string) => {
    AddUser = { ...AddUser, emailErrorText: error }
    mergeBookingDetailPermissions({
      addUser: AddUser,
    })
  }

  const handleChangeRole = (e: SelectChangeEvent<unknown>) => {
    AddUser = { ...AddUser, role: e?.target?.value as xRMApiPermissionRole }
    mergeBookingDetailPermissions({
      addUser: AddUser,
    })
  }

  const handleValidationRole = (error?: string) => {
    AddUser = { ...AddUser, roleErrorText: error }
    mergeBookingDetailPermissions({
      addUser: AddUser,
    })
  }

  const onAddUser = () => {
    postPermission({
      salesorderId: currentSalesorderId ?? undefined,
      email: AddUser.email,
      role: AddUser.role,
    }).then((res: xRMApiPermission) => {
      const { permissions } = getBookingDetailPermissions()
      const Permissions = JSON.parse(
        JSON.stringify(permissions),
      ) as xRMApiPermission[]
      const permissionExist = Permissions.some((el) => el.id === res.id)

      if (AddUser.role == "BuyOnForeignAccount") {
        res.emailForeignAccount = account?.idTokenClaims?.email as string
      }

      if (permissionExist) {
        mergeBookingDetailPermissions({
          addUser: {
            open: false,
            email: "",
            role: "Administrator",
          },
        })
        mergeBookingDetailPermission(res.id ?? "", res)
        return
      }
      mergeBookingDetailPermissions({
        addUser: {
          open: false,
          email: "",
          role: "Administrator",
        },
        permissions: [...Permissions, res],
      })
    })
  }

  return (
    <Dialog
      closeOnClick
      open={useMemo(() => addUser.open, [addUser.open])}
      title={m.title}
      firstRowButtons={[
        {
          disabled: disableAddUserButton,
          title: m.buttons.add,
          variant: "text",
          onClick: onAddUser,
        },
        {
          title: m.buttons.cancel,
          variant: "text",
        },
      ]}
      onClose={() =>
        mergeBookingDetailPermissions({
          addUser: {
            open: !addUser.open,
            email: addUser.email,
            role: addUser.role,
          },
        })
      }
    >
      <div className={bem("form")}>
        <div className={bem("form__field")}>
          <InputField
            required
            errorText={addUser.emailErrorText}
            label={m.form.email}
            messages={messages}
            name="email"
            type="email"
            value={addUser.email}
            onChange={handleChangeEmail}
            onValidate={handleValidationEmail}
          />
        </div>
        <div className={bem("form__field")}>
          <Select
            required
            errorText={addUser.roleErrorText}
            items={permissionItems}
            label={m.form.role}
            messages={messages}
            name="role"
            value={addUser.role}
            onChange={handleChangeRole}
            onValidate={handleValidationRole}
          />
        </div>
      </div>
    </Dialog>
  )
}
